/**
 * Фича «Поставить дверь или окно».
 * Проём всегда принадлежит стене, поэтому ставится кликом по самой стене:
 * точку клика проецируем на ось стены и получаем смещение вдоль неё.
 * После установки автоматически возвращаемся к инструменту выделения —
 * так удобнее сразу поправить размеры в панели свойств.
 * Дверь или окно можно и перетащить из раздела «Проёмы» (`dropAt`): проём встаёт на
 * ближайшую к точке отпускания стену, если она в пределах `DROP_SNAP`. Так же ставит и
 * клик рядом со стеной.
 * Пока инструмент выбран или проём тянут из панели, за курсором по ближайшей стене едет
 * призрак (`ghost`) — ровно там и такого размера, каким проём встанет: холст рисует его
 * и размеры от краёв проёма до углов стены.
 */
import { useState } from "react";
import { dist, projectOnSegment, type Pt } from "@/shared/lib/geometry";
import { clampOffset, OPENING_LIMITS, wallLength, type Opening } from "@/shared/plan";
import { SelectionKind, Tool, useEditorStore } from "@/shared/store";
import { useWallsStore } from "@/shared/store/layers/wallsStore";
import { useProjectStore } from "@/shared/store";

/** Дальше этого от оси стены отпущенный проём ни на какую стену не встаёт, мм. */
const DROP_SNAP = 450;
/** Id призрака проёма — не совпадает ни с одним настоящим */
const GHOST_ID = "opening-ghost";

export const usePlaceOpening = () => {
	const tool = useEditorStore((s) => s.tool);
	const setTool = useEditorStore((s) => s.setTool);
	const setSelection = useEditorStore((s) => s.setSelection);
	const openingSettings = useEditorStore((s) => s.openingSettings);
	const addOpening = useWallsStore((s) => s.addOpening);
	const plan = useProjectStore((s) => s.project.layers.walls);

	const kind = tool === Tool.Door ? "door" : "window";
	const active = tool === Tool.Door || tool === Tool.Window;
	const [cursor, setCursor] = useState<Pt | null>(null);

	/** Ближайшая к точке стена в пределах `DROP_SNAP`; null — стены рядом нет. */
	const nearestWall = (world: Pt): string | null => {
		let nearest: string | null = null;
		let nearestDistance = DROP_SNAP;
		for (const wall of Object.values(plan.walls)) {
			const a = plan.nodes[wall.a];
			const b = plan.nodes[wall.b];
			if (!a || !b) continue;
			const { distance } = projectOnSegment(world, a, b);
			if (distance < nearestDistance) {
				nearestDistance = distance;
				nearest = wall.id;
			}
		}
		return nearest;
	};

	/**
	 * Призрак проёма под курсором: ширина и место — те же, что выставит `addOpening`
	 * (ширина ужата по стене, проём не вылезает за её концы).
	 */
	const ghost = ((): Opening | null => {
		if (!active || !cursor) return null;
		const wallId = nearestWall(cursor);
		const wall = wallId ? plan.walls[wallId] : undefined;
		const a = wall ? plan.nodes[wall.a] : undefined;
		const b = wall ? plan.nodes[wall.b] : undefined;
		if (!wall || !a || !b) return null;
		const length = wallLength(plan, wall);
		if (length < 500) return null;
		const preset = openingSettings[kind];
		const width = Math.min(preset.width, Math.max(OPENING_LIMITS.minWidth, length - 200));
		const offset = projectOnSegment(cursor, a, b).t * dist(a, b);
		return {
			id: GHOST_ID,
			wallId: wall.id,
			kind,
			item: preset.item,
			offset: clampOffset(offset, width, length),
			width,
			height: preset.height,
			sill: preset.sill,
		};
	})();

	const placeOnWall = (wallId: string, world: Pt): boolean => {
		if (!active) return false;
		const wall = plan.walls[wallId];
		if (!wall) return false;
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) return false;

		const projection = projectOnSegment(world, a, b);
		// Поставленный проём — свой шаг истории: Ctrl+Z убирает именно его
		const id = useProjectStore
			.getState()
			.recordEdit(() => addOpening(wallId, kind, projection.t * dist(a, b), openingSettings[kind]));
		if (id) setSelection({ kind: SelectionKind.Opening, id });
		setTool(Tool.Select);
		setCursor(null);
		return true;
	};

	/**
	 * Проём отпустили над холстом после перетаскивания: ставим на ближайшую стену.
	 * false — стены рядом нет, ставить некуда.
	 */
	const dropAt = (world: Pt): boolean => {
		const nearest = nearestWall(world);
		return nearest !== null && placeOnWall(nearest, world);
	};

	/** Курсор над холстом — за ним едет призрак; null — курсор ушёл с холста. */
	const trackCursor = (world: Pt | null) => setCursor(world);

	return { active, ghost, placeOnWall, dropAt, trackCursor };
};
